// ═══════════════════════════════════════════════════════════════
// MIGRACIÓN: /accesos (legacy) → /asignaciones (shape v2)
// ═══════════════════════════════════════════════════════════════
//
// Convierte la colección /accesos (modelo legacy persona-empresa-permiso)
// a /asignaciones con shape v2 (modelo nuevo: persona-empresa-scope).
//
// Diferencia clave: el modelo v2 NO mezcla rol con asignación.
//   - La ASIGNACIÓN dice "este usuario tiene acceso a esta empresa".
//   - El ROL del usuario (en /usuarios.rolId, asignado en B3) define qué PUEDE HACER.
//   - El campo legacy `permiso: VIEWER/EDITOR/ADMIN` desaparece — solo se preserva
//     en `_origenAccesos` para auditoría.
//
// SHAPE v2 que se crea:
//   {
//     userId,
//     scope: {
//       tipo: 'sistema' | 'empresa' | 'grupo' | 'supervision',
//       empresaId?,       sistemaTotal?,     wizard?,
//       grupoId?,         consultoresIds?,
//     },
//     desde, hasta?, esActivo,
//     creadoPor, creadoEn, actualizadoEn,
//     _origenAccesos?: { accesoId, recursoTipo, permiso, modulos }  // auditoría
//   }
//
// IDS DETERMINISTAS:
//   asignacionId = `${userId}__${scopeKey}` donde scopeKey codifica el scope.
//   Esto hace el script idempotente: re-run no duplica docs.
//
// ⚠️ ANTES de migrar /accesos, el script CREA EL WIZARD DE MARIANO PRIMERO.
// Si esa creación falla, abortamos todo. Es el seguro de no perder acceso.
//
// Uso (desde consola admin del navegador, logueado):
//   await __asegurarWizardMariano()           ← paso 1: verificar Mariano OK
//   await __migrateAccesosAAsignacionesV2({ dryRun: true })  ← paso 2: dry-run
//   await __migrateAccesosAAsignacionesV2()                  ← paso 3: real
//
// Idempotente. Re-correr no rompe nada.
//
// NO BORRA /accesos — eso pasa en B9 (cleanup) cuando todo el sistema
// consume la colección nueva y nadie depende de la vieja.
// ═══════════════════════════════════════════════════════════════

import {
  collection, getDocs, getDoc, setDoc, doc, query, where, serverTimestamp,
} from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { docConId } from '@core/lib/docConId'
import { COLLECTIONS } from '@core/lib/constants'

// ─────────────────────────────────────────────────────────────────
// Configuración del Wizard
// ─────────────────────────────────────────────────────────────────

const MARIANO_EMAIL = 'mariano@milbrands.com.ar'
const WIZARD_ID_PREFIX = 'wizard'  // → asignacionId = `wizard__${marianoUid}`

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

/**
 * Genera una clave determinística para un scope. Usada como sufijo del docId
 * para garantizar idempotencia (mismo userId + mismo scope → mismo docId).
 */
function scopeKey(scope) {
  if (!scope || typeof scope !== 'object') return 'invalid'
  if (scope.sistemaTotal === true && scope.wizard === true) return 'wizard'
  if (scope.sistemaTotal === true) return 'sistema'
  if (scope.tipo === 'empresa' && scope.empresaId) return `empresa_${scope.empresaId}`
  if (scope.tipo === 'grupo' && scope.grupoId) return `grupo_${scope.grupoId}`
  if (scope.tipo === 'supervision' && Array.isArray(scope.consultoresIds)) {
    return `supervision_${scope.consultoresIds.slice().sort().join('-')}`
  }
  return `unknown_${scope.tipo || 'x'}`
}

function asignacionIdParaUserScope(userId, scope) {
  return `${userId}__${scopeKey(scope)}`
}

/**
 * Busca el doc del usuario por email. Retorna null si no existe.
 */
async function getUsuarioPorEmail(email) {
  const q = query(
    collection(db, COLLECTIONS.USUARIOS),
    where('email', '==', email.toLowerCase()),
  )
  const snap = await getDocs(q)
  if (snap.empty) return null
  // Si hay varios docs (raro pero posible), preferir el que tenga uid==id (real Firebase Auth).
  const docs = snap.docs.map(docConId)
  const real = docs.find(d => d.uid === d.id) || docs[0]
  return real
}

// ─────────────────────────────────────────────────────────────────
// PASO 1: ASEGURAR EL WIZARD DE MARIANO (CRÍTICO)
// ─────────────────────────────────────────────────────────────────

/**
 * Crea o verifica la asignación Wizard de Mariano.
 * ESTE ES EL SEGURO ANTI-LOCKOUT — si falla, no avanzamos con migración.
 *
 * @returns {Promise<{ok: true, marianoUid: string, asignacionId: string} | {ok: false, error: string}>}
 */
export async function asegurarWizardMariano() {
  console.groupCollapsed('[asegurarWizardMariano]')
  try {
    const mariano = await getUsuarioPorEmail(MARIANO_EMAIL)
    if (!mariano) {
      const err = `Usuario ${MARIANO_EMAIL} no encontrado en /usuarios. Abortar.`
      console.error(err)
      console.groupEnd()
      return { ok: false, error: err }
    }
    const marianoUid = mariano.uid || mariano.id
    console.log(`  Mariano encontrado: uid=${marianoUid}`)

    const wizardScope = { tipo: 'sistema', sistemaTotal: true, wizard: true }
    const asignacionId = `${WIZARD_ID_PREFIX}__${marianoUid}`
    const ref = doc(db, COLLECTIONS.ASIGNACIONES, asignacionId)
    const snap = await getDoc(ref)

    const payload = {
      userId: marianoUid,
      scope: wizardScope,
      desde: snap.exists() ? (snap.data().desde || serverTimestamp()) : serverTimestamp(),
      hasta: null,
      esActivo: true,
      creadoPor: marianoUid,  // self-creado
      actualizadoEn: serverTimestamp(),
      schemaVersion: 2,
      _origen: 'asegurarWizardMariano',
    }
    if (!snap.exists()) {
      payload.creadoEn = serverTimestamp()
      await setDoc(ref, payload)
      console.log(`  ✅ CREADO wizard asignacionId=${asignacionId}`)
    } else {
      await setDoc(ref, payload, { merge: true })
      console.log(`  ✅ Wizard YA EXISTÍA. Refrescado (esActivo=true, scope.wizard=true).`)
    }

    console.groupEnd()
    return { ok: true, marianoUid, asignacionId }
  } catch (e) {
    console.error('[asegurarWizardMariano] error:', e)
    console.groupEnd()
    return { ok: false, error: e.message || String(e) }
  }
}

// ─────────────────────────────────────────────────────────────────
// PASO 2: MIGRAR /accesos → /asignaciones (shape v2)
// ─────────────────────────────────────────────────────────────────

/**
 * Mapea un doc legacy de /accesos al shape v2 de /asignaciones.
 *
 * Reglas:
 *   - recursoTipo='SISTEMA'              → scope.tipo='sistema', sistemaTotal=true (solo si permiso=ADMIN)
 *   - recursoTipo='EMPRESA' + recursoId  → scope.tipo='empresa', empresaId=recursoId
 *   - Otros recursoTipo → ignoramos (raro, deberíamos loggear)
 *
 * El campo `permiso` (VIEWER/EDITOR/ADMIN) NO se mapea — solo se preserva
 * en `_origenAccesos` para auditoría. El rol del usuario (asignado en B3)
 * define qué puede hacer.
 *
 * @param {Object} acceso  — doc de /accesos
 * @returns {Object|null}  — payload de asignación v2 o null si no se puede mapear
 */
function mapearAccesoAAsignacion(acceso) {
  if (!acceso || !acceso.userId) return null

  let scope = null
  if (acceso.recursoTipo === 'SISTEMA') {
    // Acceso SISTEMA solo tiene sentido como permiso ADMIN (Operaciones).
    // Permisos VIEWER/EDITOR a nivel sistema son raros y los ignoramos.
    if (acceso.permiso !== 'ADMIN' && acceso.permiso !== 'admin') {
      console.warn(`[mapearAcceso] SISTEMA con permiso=${acceso.permiso} ignorado (solo ADMIN tiene sentido a sistema)`)
      return null
    }
    scope = { tipo: 'sistema', sistemaTotal: true }
  } else if (acceso.recursoTipo === 'EMPRESA' && acceso.recursoId) {
    scope = { tipo: 'empresa', empresaId: acceso.recursoId }
  } else {
    console.warn(`[mapearAcceso] recursoTipo desconocido o sin recursoId:`, acceso)
    return null
  }

  return {
    userId: acceso.userId,
    scope,
    desde: acceso.creadoEn || serverTimestamp(),
    hasta: acceso.expiraEn || null,
    esActivo: acceso.activo !== false,  // si no está marcado false → activo
    creadoPor: acceso.otorgadoPor || acceso.userId,
    actualizadoEn: serverTimestamp(),
    schemaVersion: 2,
    _origenAccesos: {
      accesoId: acceso.id,
      recursoTipo: acceso.recursoTipo,
      recursoId: acceso.recursoId || null,
      permiso: acceso.permiso || null,
      modulos: Array.isArray(acceso.modulos) ? acceso.modulos : [],
      etiqueta: acceso.etiqueta || null,
    },
  }
}

/**
 * Migra todos los docs de /accesos a /asignaciones con shape v2.
 *
 * @param {{dryRun?: boolean}} opts
 * @returns {Promise<{
 *   wizard: { ok: boolean, marianoUid?: string },
 *   migrados: number,
 *   ignorados: number,
 *   detalles: Array
 * }>}
 */
export async function migrateAccesosAAsignacionesV2(opts = {}) {
  const { dryRun = false } = opts
  console.groupCollapsed(`[migrateAccesosAAsignacionesV2] dryRun=${dryRun}`)

  // ─── Paso 1: SEGURO. Crear/verificar Wizard de Mariano. ───
  const wizardCheck = await asegurarWizardMariano()
  if (!wizardCheck.ok) {
    console.error('🚨 ABORTANDO: no se pudo asegurar el Wizard de Mariano.')
    console.error('   Error:', wizardCheck.error)
    console.error('   No vamos a migrar más nada — se corre riesgo de lockout.')
    console.groupEnd()
    return {
      wizard: { ok: false, error: wizardCheck.error },
      migrados: 0,
      ignorados: 0,
      detalles: [],
    }
  }
  console.log(`  ✓ Wizard de Mariano OK (uid=${wizardCheck.marianoUid})`)

  // ─── Paso 2: Leer /accesos ───
  const snap = await getDocs(collection(db, COLLECTIONS.ACCESOS))
  console.log(`  ${snap.size} docs encontrados en /accesos`)

  let migrados = 0
  let ignorados = 0
  const detalles = []

  for (const d of snap.docs) {
    const acceso = docConId(d)
    const payload = mapearAccesoAAsignacion(acceso)
    if (!payload) {
      ignorados++
      detalles.push({ accesoId: acceso.id, accion: 'ignorado', motivo: 'no mappable' })
      continue
    }

    const asignacionId = asignacionIdParaUserScope(payload.userId, payload.scope)
    const ref = doc(db, COLLECTIONS.ASIGNACIONES, asignacionId)
    const existeYa = await getDoc(ref)

    if (!dryRun) {
      if (!existeYa.exists()) {
        await setDoc(ref, { ...payload, creadoEn: serverTimestamp() })
      } else {
        // Merge — no pisar campos que el admin podría haber tocado (hasta, esActivo manual).
        await setDoc(ref, payload, { merge: true })
      }
    }

    migrados++
    detalles.push({
      accesoId: acceso.id,
      asignacionId,
      userId: payload.userId,
      scope: payload.scope,
      accion: existeYa.exists() ? 'merge' : 'creado',
    })
  }

  console.log(`  ✓ migrados=${migrados}, ignorados=${ignorados}`)
  console.groupEnd()

  return {
    wizard: { ok: true, marianoUid: wizardCheck.marianoUid, asignacionId: wizardCheck.asignacionId },
    migrados,
    ignorados,
    detalles,
  }
}

// ─────────────────────────────────────────────────────────────────
// PASO 3: VERIFICACIÓN POST-MIGRACIÓN
// ─────────────────────────────────────────────────────────────────

/**
 * Lee /asignaciones y devuelve un resumen de cuántas hay por scope.tipo + cuántos
 * usuarios tienen al menos una asignación. Útil para validar post-migración.
 */
export async function verificarAsignaciones() {
  const snap = await getDocs(collection(db, COLLECTIONS.ASIGNACIONES))
  const docs = snap.docs.map(docConId)

  const totales = {
    total: docs.length,
    activas: 0,
    porTipo: {},
    wizards: [],
    sistemaSinWizard: [],
    usuariosUnicos: new Set(),
  }

  for (const a of docs) {
    if (a.esActivo !== false) totales.activas++
    const tipo = a.scope?.tipo || 'desconocido'
    totales.porTipo[tipo] = (totales.porTipo[tipo] || 0) + 1
    totales.usuariosUnicos.add(a.userId)

    if (a.scope?.wizard === true) {
      totales.wizards.push({ asignacionId: a.id, userId: a.userId })
    } else if (a.scope?.sistemaTotal === true) {
      totales.sistemaSinWizard.push({ asignacionId: a.id, userId: a.userId })
    }
  }

  console.group('[verificarAsignaciones]')
  console.log(`  Total: ${totales.total}`)
  console.log(`  Activas: ${totales.activas}`)
  console.log(`  Por tipo:`, totales.porTipo)
  console.log(`  Usuarios únicos con asignación: ${totales.usuariosUnicos.size}`)
  console.log(`  WIZARDS: ${totales.wizards.length}`, totales.wizards)
  console.log(`  Sistema sin wizard (Operaciones): ${totales.sistemaSinWizard.length}`, totales.sistemaSinWizard)
  console.groupEnd()

  return {
    ...totales,
    usuariosUnicos: totales.usuariosUnicos.size,
  }
}

// ─────────────────────────────────────────────────────────────────
// EXPOSICIÓN EN WINDOW
// ─────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-undef
  window.__asegurarWizardMariano = asegurarWizardMariano
  // eslint-disable-next-line no-undef
  window.__migrateAccesosAAsignacionesV2 = migrateAccesosAAsignacionesV2
  // eslint-disable-next-line no-undef
  window.__verificarAsignaciones = verificarAsignaciones
}
